/**
 * Users feature API layer — OpenAPI-first (§9.1, §9.3).
 *
 * Wraps orval-generated client for directory users CRUD + import oracle.
 */

import {
  getAdminDirectoryUsersListQueryKey,
  useAdminDirectoryUsersCreate as useGenCreate,
  useAdminDirectoryUsersDelete as useGenDelete,
  useAdminDirectoryUsersGet as useGenGet,
  useAdminUsersImportOracle as useGenImportOracle,
  useAdminDirectoryUsersList as useGenList,
  useAdminDirectoryUsersUpdate as useGenUpdate,
} from "@shared/api/generated/admin/admin";
import type { AdminDirectoryUsersListParams } from "@shared/api/generated/model";
import { useQueryClient } from "@tanstack/react-query";

/** Re-export DirectoryUser as AdminUser for backward compatibility. */
export type {
  DirectoryContact,
  DirectoryUser,
  DirectoryUser as AdminUser,
  DirectoryUserObject,
  ImportOracleUsersResponse,
} from "@shared/api/generated/model";

// ── Filter interface (matches old hooks.ts API) ─────────────────────────────

export interface AdminUserFilters {
  name?: string;
  contact?: string;
  object?: { regionId: number; objectId: number };
}

// ── Queries ─────────────────────────────────────────────────────────────────

export const useAdminUsers = (filters?: AdminUserFilters) => {
  const params: AdminDirectoryUsersListParams = {
    ...(filters?.name && { name: filters.name }),
    ...(filters?.contact && { contact: filters.contact }),
    ...(filters?.object && {
      region: filters.object.regionId,
      object: filters.object.objectId,
    }),
  };
  return useGenList(params, { query: { staleTime: 60_000 } });
};

export const useAdminUser = (id?: string) => {
  const enabled = Boolean(id && id !== "new");
  return useGenGet(id ?? "", { query: { enabled, staleTime: 60_000 } });
};

// ── Mutations ───────────────────────────────────────────────────────────────

export const useCreateAdminUser = () => {
  const qc = useQueryClient();
  return useGenCreate({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getAdminDirectoryUsersListQueryKey() });
      },
    },
  });
};

export const useUpdateAdminUser = () => {
  const qc = useQueryClient();
  return useGenUpdate({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getAdminDirectoryUsersListQueryKey() });
      },
    },
  });
};

export const useDeleteDirectoryUser = () => {
  const qc = useQueryClient();
  return useGenDelete({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getAdminDirectoryUsersListQueryKey() });
      },
    },
  });
};

export const useImportOracleUsers = () => {
  const qc = useQueryClient();
  return useGenImportOracle({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getAdminDirectoryUsersListQueryKey() });
      },
    },
  });
};
